<script setup>
import { computed, onMounted, watch, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
    createGoogleAccount,
    getDeviceToken,
    loginUser,
    setDeviceToken,
} from '@/services/api/auth'
import { handleLoginError } from '@/utils/errors'
import { useI18n } from 'vue-i18n'
import LanguageSwitcherHorizontal from '@/components/common/LanguageSwitcherHorizontal.vue'
import TwoFactorCode from '@/components/authentication/forms/TwoFactorCode.vue'
import CreateAccountForm from '@/components/authentication/forms/CreateAccountForm.vue'
import ButtonBack from '@/components/common/button/ButtonBack.vue'
import AuthBrandPanel from '@/components/authentication/AuthBrandPanel.vue'
import AuthBrandLogo from '@/components/authentication/AuthBrandLogo.vue'
import { getAnnotationById } from '@/services/api/annotation.js'
import { useAuthStore } from '@/stores/authStore.js'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const authStore = useAuthStore()

const login = ref('')
const password = ref('')
const errorMessage = ref('')
const code = ref('')
const remember = ref(false)
const loading = ref(false)
const showPassword = ref(false)
const showCodeInput = ref(false)

// Como pedir o codigo: vem na propria recusa do login (errorcode 3). Antes a
// tela adivinhava, e adivinhava errado -- quem usa codigo por e-mail era
// mandado a abrir um aplicativo autenticador que nao tem.
const metodoDoSegundoFator = ref('app')
const destinoDoCodigo = ref('')
const showCreateAccount = ref(false)

const brandContents = computed(() => ({
    default: {
        name: t('components.authentication.signIn.defaultBrand.name'),
        badge: t('components.authentication.signIn.defaultBrand.badge'),
        title: t('components.authentication.signIn.defaultBrand.title'),
        accent: t('components.authentication.signIn.defaultBrand.accent'),
        description: t('components.authentication.signIn.defaultBrand.description'),
        logo: {
            type: 'image',
            src: '/images/static/logos/logo-icon-white.webp',
            alt: t('components.authentication.signIn.defaultBrand.name'),
        },
        highlights: [
            {
                icon: 'hub',
                title: t('components.authentication.signIn.defaultBrand.ecosystemTitle'),
                description: t('components.authentication.signIn.defaultBrand.ecosystemDescription'),
            },
            {
                icon: 'description',
                title: t('components.authentication.signIn.defaultBrand.contractsTitle'),
                description: t('components.authentication.signIn.defaultBrand.contractsDescription'),
            },
            {
                icon: 'router',
                title: t('components.authentication.signIn.defaultBrand.ispTitle'),
                description: t('components.authentication.signIn.defaultBrand.ispDescription'),
            },
        ],
        trustTitle: t('components.authentication.signIn.defaultBrand.trustTitle'),
        trustText: t('components.authentication.signIn.defaultBrand.trustText'),
    },
    rhdigital: {
        name: t('components.authentication.signIn.rhdigitalBrand.name'),
        badge: t('components.authentication.signIn.rhdigitalBrand.badge'),
        title: t('components.authentication.signIn.rhdigitalBrand.title'),
        accent: t('components.authentication.signIn.rhdigitalBrand.accent'),
        description: t('components.authentication.signIn.rhdigitalBrand.description'),
        logo: {
            type: 'svg',
        },
        highlights: [
            {
                icon: 'groups',
                title: t('components.authentication.signIn.rhdigitalBrand.teamsTitle'),
                description: t('components.authentication.signIn.rhdigitalBrand.teamsDescription'),
            },
            {
                icon: 'monitoring',
                title: t('components.authentication.signIn.rhdigitalBrand.reportsTitle'),
                description: t('components.authentication.signIn.rhdigitalBrand.reportsDescription'),
            },
            {
                icon: 'lock',
                title: t('components.authentication.signIn.rhdigitalBrand.securityTitle'),
                description: t('components.authentication.signIn.rhdigitalBrand.securityDescription'),
            },
        ],
        trustTitle: t('components.authentication.signIn.rhdigitalBrand.trustTitle'),
        trustText: t('components.authentication.signIn.rhdigitalBrand.trustText'),
    },
}))

const brandContent = computed(() => {
    return route.path === '/rhdigital' ? brandContents.value.rhdigital : brandContents.value.default
})

const mobileLogoOverride = computed(() => {
    if (brandContent.value.logo?.type === 'svg') {
        return null
    }

    return {
        type: 'image',
        src: '/images/static/logos/logo-icon.png',
        alt: brandContent.value.name,
    }
})

const metrics = computed(() => [
    { value: '1.200+', label: t('components.authentication.signIn.metrics.employees') },
    { value: '48', label: t('components.authentication.signIn.metrics.departments') },
    { value: '100%', label: t('components.authentication.signIn.metrics.digitalManagement') },
])

const professionals = [
    { initials: 'AJ', color: '#605dff' },
    { initials: 'CM', color: '#17cf97' },
    { initials: 'FC', color: '#ee368c' },
    { initials: 'RB', color: '#fd7e14' },
    { initials: 'DM', color: '#0ea5e9' },
]

const isRHDigitalLogin = computed(() => authStore.signInRoute === '/rhdigital')

const syncAuthenticationEntryPath = () => {
    authStore.setAuthenticationEntryPath(route.path)
}

onMounted(syncAuthenticationEntryPath)
watch(() => route.path, syncAuthenticationEntryPath)

const togglePassword = () => {
    showPassword.value = !showPassword.value
}

const openCreateAccount = () => {
    showCreateAccount.value = true
    errorMessage.value = ''
}

const backToLogin = () => {
    showCreateAccount.value = false
    errorMessage.value = ''
}

const handleLogin = async ({ corp = '*' } = {}) => {
    try {
        errorMessage.value = ''
        loading.value = true

        const credentials = {
            login: login.value,
            password: password.value,
            corp,
            device_token: getDeviceToken(),
        }

        if (code.value) {
            credentials.two_factor_code = code.value
            credentials.remember_device = remember.value
        }

        const response = await loginUser(credentials)
        const userCorps = response.credentials?.user?.corps ?? {}
        const userCorpsCount = Object.keys(userCorps).length

        const hasAnnotation = await getAnnotationById({
            id: `ModuleTotem${response.credentials?.user?.id}`,
        }).catch(() => null)

        if (isRHDigitalLogin.value && userCorpsCount === 0) {
            authStore.setCompanySetupRedirectRoute('/rhdigital/mural')
            router.push('/rhdigital/company-setup')
            return
        }

        const nextRoute = hasAnnotation
            ? 'kiosk'
            : userCorpsCount > 1 && corp === '*'
                ? { name: isRHDigitalLogin.value ? 'rhdigital-corporation-selection' : 'corporation-selection' }
                : isRHDigitalLogin.value
                    ? '/rhdigital/mural'
                    : '/home'

        router.push(nextRoute)
    } catch (error) {
        const errorCode = error.response?.data?.errorcode
        const status = error.status
        const firstAccessTwoFactorCode = 428

        if (status === firstAccessTwoFactorCode) {
            setDeviceToken('')
            router.push('/two-factor')
            return
        }

        if (errorCode === 3 || errorCode === 2) {
            if (errorCode === 2) {
                errorMessage.value = await handleLoginError(error, t)
            }

            // `?? 'app'` cobre a API antiga, que nao manda o metodo: ali o
            // comportamento continua o de antes em vez de ficar em branco.
            metodoDoSegundoFator.value = error.response?.data?.two_factor_method ?? 'app'
            destinoDoCodigo.value = error.response?.data?.two_factor_destino ?? ''

            showCodeInput.value = true
            return
        }

        // `posLogin`: este try tambem cobre setCorp e appmenu, entao um 401/403
        // sem errorcode aqui e "autenticou, mas sem permissao", nao falha interna.
        errorMessage.value = await handleLoginError(error, t, { posLogin: true })
        console.error(error)
    } finally {
        loading.value = false
    }
}

const handleSubmitCode = ({ code: codeValue, remember: rememberValue }) => {
    code.value = codeValue
    remember.value = rememberValue
    setDeviceToken('')
    handleLogin()
}

const handleGoogleLogin = async () => {
    await createGoogleAccount()
    message.info(t('components.authentication.signIn.googleIntegrationSoon'))
}

</script>

<template>
    <div class="sign-in-area">
        <AuthBrandPanel
            :content="brandContent"
            :metrics="metrics"
            :professionals="professionals"
        />

        <main class="login-panel">
            <section class="login-panel__content">
                <AuthBrandLogo
                    :content="brandContent"
                    :logo-override="mobileLogoOverride"
                    class="login-panel__mobile-logo auth-brand-logo--mobile"
                />

                <div class="login-panel__header">
                    <h2 class="text-center">
                        {{
                            showCodeInput
                                ? $t('components.authentication.signIn.twoFactorTitle')
                                : showCreateAccount
                                    ? $t('components.authentication.signIn.createAccountTitle')
                                    : $t('components.authentication.signIn.accessAccount')
                        }}
                    </h2>
                    <p class="text-center">
                        {{
                            showCodeInput
                                ? $t('components.authentication.signIn.twoFactorSubtitle')
                                : showCreateAccount
                                    ? $t('components.authentication.signIn.createAccountSubtitle')
                                    : $t('components.authentication.signIn.accessAccountSubtitle')
                        }}
                    </p>
                </div>

                <div class="login-card">
                    <div v-if="errorMessage" class="login-alert">
                        {{ errorMessage }}
                    </div>

                    <CreateAccountForm
                        v-if="showCreateAccount && !showCodeInput"
                        @back="backToLogin"
                    />

                    <form v-else-if="!showCodeInput" class="login-form" @submit.prevent="handleLogin">
                        <div class="login-form__group">
                            <label for="login">{{ $t('common.labels.login') }}</label>
                            <div class="login-form__field">
                                <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                    <polyline points="22,6 12,13 2,6" />
                                </svg>
                                <input
                                    id="login"
                                    v-model="login"
                                    type="text"
                                    :placeholder="$t('components.authentication.signIn.loginPlaceholder')"
                                    autocomplete="username"
                                    required
                                >
                            </div>
                        </div>

                        <div class="login-form__group">
                            <div class="login-form__label-row">
                                <label for="password">{{ $t('common.labels.password') }}</label>
                                <RouterLink to="/forgot-password">
                                    {{ $t('components.authentication.signIn.forgotPassword') }}
                                </RouterLink>
                            </div>

                            <div class="login-form__field">
                                <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                    <path d="M7 11V7a5 5 0 0110 0v4" />
                                </svg>
                                <input
                                    id="password"
                                    v-model="password"
                                    :type="showPassword ? 'text' : 'password'"
                                    :placeholder="$t('components.authentication.signIn.passwordPlaceholder')"
                                    autocomplete="current-password"
                                    required
                                >
                                <button
                                    class="login-form__visibility"
                                    type="button"
                                    :aria-label="showPassword ? $t('components.authentication.signIn.hidePassword') : $t('components.authentication.signIn.showPassword')"
                                    @click="togglePassword"
                                >
                                    <span class="material-symbols-outlined">
                                        {{ showPassword ? 'visibility_off' : 'visibility' }}
                                    </span>
                                </button>
                            </div>
                        </div>

                        <button class="login-form__submit" type="submit" :disabled="loading">
                            <span v-if="loading" class="login-form__spinner" aria-hidden="true"></span>
                            <span>{{ loading ? $t('components.authentication.signIn.entering') : $t('components.authentication.signIn.enterPlatform') }}</span>
                            <svg v-if="!loading" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                                <path d="M5 12h14M12 5l7 7-7 7" />
                            </svg>
                        </button>
                    </form>

                    <div v-else class="two-factor-card">
                        <TwoFactorCode
                            :metodo="metodoDoSegundoFator"
                            :destino="destinoDoCodigo"
                            @submit="handleSubmitCode"
                        />
                        <ButtonBack
                            class="two-factor-card__back"
                            route=""
                            @click="showCodeInput = false; code = ''; errorMessage = ''"
                        />
                    </div>
                </div>

                <div v-if="!showCodeInput && !showCreateAccount" class="login-panel__divider">
                    <span></span>
                    <small>{{ $t('components.authentication.signIn.orAccessWith') }}</small>
                    <span></span>
                </div>

                <button
                    v-if="!showCodeInput && !showCreateAccount"
                    class="google-button"
                    type="button"
                    @click="handleGoogleLogin"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                    </svg>
                    {{ $t('components.authentication.signIn.googleLogin') }}
                </button>

                <p v-if="!showCodeInput && !showCreateAccount" class="login-panel__create-account">
                    {{ $t('components.authentication.signIn.noAccount') }}
                    <button type="button" @click="openCreateAccount">
                        {{ $t('components.authentication.signIn.createOne') }}
                    </button>
                </p>
                <LanguageSwitcherHorizontal class="login-panel__languages" />
            </section>
        </main>
    </div>
</template>

<style lang="scss" scoped>
.sign-in-area {
    min-height: 100vh;
    display: flex;
    font-family: Inter, sans-serif;
    background: #f8f9ff;
}

.login-panel {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 32px;
    background: #f8f9ff;

    &__content {
        width: 100%;
        max-width: 420px;
    }

    &__header {
        margin-bottom: 32px;

        h2 {
            margin: 0 0 8px;
            color: #260944;
            font-size: 28px;
            font-weight: 800;
            line-height: 1.2;
        }

        p {
            margin: 0;
            color: #a9a9c8;
            font-size: 14px;
            line-height: 1.65;
        }
    }

    &__divider {
        display: flex;
        align-items: center;
        gap: 14px;
        margin: 22px 0;

        span {
            flex: 1;
            height: 1px;
            background: #eceef2;
        }

        small {
            color: #a9a9c8;
            font-size: 12px;
            font-weight: 500;
            white-space: nowrap;
        }
    }

    &__support {
        margin: 20px 0 0;
        color: #a9a9c8;
        font-size: 12px;
        text-align: center;

        a {
            color: #605dff;
            font-weight: 600;
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    &__create-account {
        margin: 18px 0 0;
        color: #a9a9c8;
        font-size: 13px;
        text-align: center;

        button {
            padding: 0;
            border: 0;
            background: transparent;
            color: #605dff;
            font: inherit;
            font-weight: 700;
            cursor: pointer;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    &__languages {
        margin-top: 18px;
    }

    &__mobile-logo {
        display: none !important;
    }
}

.login-card {
    padding: 36px;
    border: 1px solid #eceef2;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 8px 40px rgba(96, 93, 255, 0.1);
}

.login-alert {
    margin-bottom: 18px;
    padding: 12px 14px;
    border: 1px solid rgba(220, 53, 69, 0.16);
    border-radius: 10px;
    background: rgba(220, 53, 69, 0.08);
    color: #b42334;
    font-size: 13px;
    line-height: 1.45;
}

.login-form {
    display: flex;
    flex-direction: column;
    gap: 20px;

    &__group {
        label {
            display: block;
            margin-bottom: 8px;
            color: #5b5b98;
            font-size: 13px;
            font-weight: 600;
        }
    }

    &__label-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 14px;
        margin-bottom: 8px;

        label {
            margin-bottom: 0;
        }

        a {
            flex-shrink: 0;
            color: #605dff;
            font-size: 12px;
            font-weight: 600;
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }
        }
    }

    &__field {
        position: relative;
        color: #a9a9c8;

        > svg {
            position: absolute;
            top: 50%;
            left: 13px;
            transform: translateY(-50%);
            transition: color 0.18s;
            pointer-events: none;
        }

        input {
            width: 100%;
            min-height: 46px;
            padding: 13px 46px 13px 40px;
            border: 1.5px solid #eceef2;
            border-radius: 10px;
            outline: none;
            background: #f8f9ff;
            color: #260944;
            font-family: Inter, sans-serif;
            font-size: 14px;
            transition: border-color 0.18s, background 0.18s, box-shadow 0.18s;

            &::placeholder {
                color: #a9a9c8;
            }

            &:focus {
                border-color: #605dff;
                background: #fff;
                box-shadow: 0 0 0 4px rgba(96, 93, 255, 0.08);
            }
        }

        &:focus-within {
            color: #605dff;
        }
    }

    &__visibility {
        position: absolute;
        top: 50%;
        right: 13px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        padding: 0;
        border: 0;
        background: transparent;
        color: #a9a9c8;
        cursor: pointer;
        transform: translateY(-50%);

        span {
            font-size: 19px;
        }

        &:hover {
            color: #605dff;
        }
    }

    &__remember {
        display: flex;
        align-items: center;
        gap: 10px;
        width: fit-content;
        margin: 0;
        color: #5b5b98;
        font-size: 13px;
        cursor: pointer;

        input {
            width: 16px;
            height: 16px;
            accent-color: #605dff;
            cursor: pointer;
        }
    }

    &__submit {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        width: 100%;
        margin-top: 4px;
        padding: 15px;
        border: 0;
        border-radius: 10px;
        background: linear-gradient(135deg, #ffd056 0%, #ffe7389e 100%);
        box-shadow: 0 6px 24px rgba(96, 93, 255, 0.38);
        color: #2c2c2cd0;
        font-family: Inter, sans-serif;
        font-size: 15px;
        font-weight: 700;
        cursor: pointer;
        transition: box-shadow 0.2s, transform 0.2s, opacity 0.2s;

        &:hover:not(:disabled) {
            box-shadow: 0 10px 28px rgba(96, 93, 255, 0.42);
            transform: translateY(-1px);
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.72;
        }
    }

    &__spinner {
        width: 16px;
        height: 16px;
        border: 2px solid rgba(255, 255, 255, 0.42);
        border-top-color: #fff;
        border-radius: 50%;
        animation: trezo-spin 0.8s linear infinite;
    }
}

.google-button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    padding: 13px;
    border: 1.5px solid #eceef2;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    color: #260944;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.72;
    }
}

.two-factor-card {
    :deep(form) {
        width: 100%;
    }

    :deep(.v-label),
    :deep(.text-black),
    :deep(h5) {
        color: #260944 !important;
    }

    :deep(.v-btn) {
        border-radius: 10px;
    }

    &__back {
        margin-top: 20px;
    }
}

@keyframes trezo-spin {
    to {
        transform: rotate(360deg);
    }
}

@media only screen and (max-width: 960px) {
    .sign-in-area {
        display: flex;
        flex-direction: column-reverse;
        background: #f8f9ff;
    }

    .login-panel {
        padding: 42px 24px;

        &__mobile-logo {
            display: flex !important;
            width: fit-content;
            margin: 0 auto 28px;
            padding: 10px 14px;
            border-radius: 14px;
        }
    }
}

@media only screen and (max-width: 575px) {
    .login-panel {
        padding: 34px 16px;

        &__header {
            margin-bottom: 24px;

            h2 {
                font-size: 24px;
            }
        }
    }

    .login-card {
        padding: 24px 18px;
        border-radius: 14px;
    }

    .login-form {
        &__label-row {
            align-items: flex-start;
            flex-direction: column;
            gap: 4px;
        }
    }
}
</style>
